import {
    FormEvent,
    InternalStatusCodes
} from "../../../core/entities/formEvent";
import { formatDateDDMMYYHHMM } from "../../../core/functions/DateFormatUtility";
import { ReactComponent as Warning } from "../../../resources/icons/warning-triangle.svg";
import { ReactComponent as Success } from "../../../resources/icons/succes-circle-checkmark.svg";
import { ReactComponent as Error } from "../../../resources/icons/error-circle-cross.svg";
import Tooltip from "../tooltip-icon/Tooltip";
import { mapErrorCodeToErrorText } from "../../../core/functions/MapErrorCodeToErrorText";
import { mapErrorCodesToErrorDescription } from "../../../core/functions/MapErrorCodesToErrorDescription";

interface Props {
    events: FormEvent[];
}

export function Timeline({ events }: Props) {
    const timelineItems = events
        .sort(
            (a, b) =>
                new Date(a.statusEventTimeUtc).getTime() -
                new Date(b.statusEventTimeUtc).getTime()
        )
        .map((e, index) => (
            <TimelineItem key={e.id} index={`${index + 1}`} formEvent={e} />
        ));

    return (
        <>
            <div className="flex flex-col  space-y-12 h-fit">
                {timelineItems}
            </div>
        </>
    );
}

interface TimelineItemProps {
    index: string;
    formEvent: FormEvent;
}

enum ItemState {
    Success,
    Warning,
    Error
}

function TimelineItem({ index, formEvent }: TimelineItemProps) {
    let state: ItemState = ItemState.Success;
    let errorHeader: string;
    let errorDescription: string;

    if (formEvent.errorCode) {
        state = formEvent.isStateEvent ? ItemState.Error : ItemState.Warning;
        errorHeader = mapErrorCodeToErrorText(formEvent.errorCode);
        errorDescription = mapErrorCodesToErrorDescription(
            formEvent.errorCode,
            formEvent.errorText
        );
    } else if (formEvent.status === InternalStatusCodes.DeclinedByDoctor) {
        state = ItemState.Error;
        errorHeader = formEvent.statusText;
        errorDescription = formEvent.errorText;
    } else {
        state = ItemState.Success;
    }

    let icon;
    let color;
    switch (state) {
        case ItemState.Success:
            icon = <Success />;
            color = "bg-status-success-light";
            break;
        case ItemState.Warning:
            icon = <Warning />;
            color = "bg-status-warning-light";
            break;
        case ItemState.Error:
            icon = <Error />;
            color = "bg-status-error-light";
            break;
    }

    return (
        <div className="flex flex-row bg-secondary-blue-lightest p-16 rounded-xl">
            {state === ItemState.Success ? (
                <div
                    className={
                        "w-40 h-40 text-secondary-gray-dark rounded-full flex justify-center items-center text-n font-medium " +
                        color
                    }
                >
                    {icon}
                </div>
            ) : (
                <Tooltip title={errorHeader!} description={errorDescription!}>
                    <div
                        className={
                            "w-40 h-40 text-secondary-gray-dark rounded-full flex justify-center items-center text-n font-medium " +
                            color
                        }
                    >
                        {icon}
                    </div>
                </Tooltip>
            )}

            <div className="pl-16">
                <p className="text-sx font-semibold text-secondary-gray-dark">
                    {formEvent.statusText}
                </p>
                <p className="text-sx font-normal text-secondary-gray-dark">
                    {formatDateDDMMYYHHMM(formEvent.statusEventTimeUtc)}
                </p>
            </div>
        </div>
    );
}
